import { useMutation, useQueries } from "@tanstack/react-query";
import React from "react";
import { API } from "../../API";
import {
  CaregiverSetBankInformation,
  InstaPayBankAccountTypeId,
  InstaPayBankId,
} from "../../messages";
import { STRINGS } from "../../strings";
import { translate } from "../../translate";
import { useModalV2 } from "../AppModals";
import ErrorScaffold from "../ErrorScaffold";
import LoadingScaffold from "../LoadingScaffold";
import FillBankInformationScreen from "../FillBankInformation/FillBankInformationScreen";

type Props = {
  api: API;
  onRequestGoToMain: () => void;
  onClickGoBack: () => void;
};

const EditBankInformationActivity = (props: Props) => {
  const modals = useModalV2();

  const [banksQuery, accountTypesQuery, infoQuery] = useQueries({
    queries: [
      { queryKey: ["banks"], queryFn: () => props.api.getBanks() },
      { queryKey: ["accountTypes"], queryFn: () => props.api.getBankAccountTypes() },
      {
        queryKey: ["instapay", "info"],
        queryFn: () => props.api.getInstaPayInfo(),
      },
    ],
  });

  const setBankInformation = useMutation({
    mutationFn: (body: CaregiverSetBankInformation) => props.api.updateBankInformation(body),
  });

  const handleValidSubmit = (body: {
    bankId: string;
    accountType: string;
    routingNumber: string;
    accountNumber: string;
    email: string;
  }) => {
    const modal = modals.createModal({ type: "neutral", isLoading: true, title: "" });

    setBankInformation.mutate(
      {
        bankId: InstaPayBankId.wrap(parseInt(body.bankId, 10)),
        accountType: InstaPayBankAccountTypeId.wrap(parseInt(body.accountType, 10)),
        routingNumber: body.routingNumber,
        accountNumber: body.accountNumber,
        email: body.email,
      },
      {
        onSuccess: () => {
          modal.update({
            type: "success",
            isLoading: false,
            title: translate(STRINGS.SUCCESS),
            message: translate(STRINGS.ACCOUNT_INFORMATION_UPDATED_SUCCESSFULLY),
          });
          infoQuery.refetch();
        },
        onError: () =>
          modal.update({
            type: "error",
            isLoading: false,
            title: translate(STRINGS.ERROR),
            message: translate(STRINGS.SOMETHING_WENT_WRONG),
          }),
      }
    );
  };

  if (banksQuery.isLoading || accountTypesQuery.isLoading || infoQuery.isLoading) {
    return (
      <LoadingScaffold title={translate(STRINGS.INSTAPAY)} onClickGoBack={props.onClickGoBack} />
    );
  }

  if (banksQuery.isError || accountTypesQuery.isError || infoQuery.isError) {
    return (
      <ErrorScaffold
        title={translate(STRINGS.OOPS)}
        message={translate(STRINGS.SOMETHING_WENT_WRONG)}
        onClickGoBack={props.onClickGoBack}
      />
    );
  }

  return (
    <FillBankInformationScreen
      bankAccountNames={banksQuery.data.banks}
      bankAccountTypes={accountTypesQuery.data.accountTypes}
      defaultValues={infoQuery.data.bankAccountInfoWithMail}
      title={translate(STRINGS.EDIT_ACCOUNT_DETAILS)}
      onValidSubmit={handleValidSubmit}
      onClickGoBack={props.onClickGoBack}
    />
  );
};

export default EditBankInformationActivity;
